import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/hooks/useAuth";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { I18nProvider } from "@/i18n";
import { UnitsProvider } from "@/hooks/useUnits";
import { PreviewProvider } from "@/components/admin/PreviewProvider";
import { PreviewBanner } from "@/components/admin/PreviewBanner";
import Navbar from "@/components/Navbar";
import BottomNav from "@/components/BottomNav";
import MobileTopBar from "@/components/MobileTopBar";
import Footer from "@/components/Footer";
import MobileCompactFooter from "@/components/MobileCompactFooter.tsx";
import CookieBanner from "@/components/consent/CookieBanner";
import OfflineBanner from "@/components/OfflineBanner";
import OfflineSync from "@/components/OfflineSync";
import GlobalContentAlertsBanner from "@/components/GlobalContentAlertsBanner";
import VersionBadge from "@/components/VersionBadge";
import ProtectedRoute from "@/components/ProtectedRoute";
import FeatureRoute from "@/components/FeatureRoute";
import BetaGate from "@/components/BetaGate";
import PageTransition from "@/components/PageTransition";
import ScrollToTop from "@/components/ScrollToTop";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import { Loader2 } from "lucide-react";
import { useSpotProjectionRealtime } from "@/hooks/useSpotProjectionRealtime";
import { NotificationRealtimeBridge } from "@/components/notifications/NotificationRealtimeBridge";
import { Navigate } from "react-router-dom";
import { DebugLayerProvider, DebugLayerHost } from "@/features/debug-layer";
// Registriert die bekannten Preview-Flags einmalig beim App-Start.
import "@/features/debug-layer/registry";

// ── Lazy-loaded pages ──────────────────────────────────────
// Public pages
const Index = lazy(() => import("./pages/Index"));
const DynamicContentPage = lazy(() => import("./pages/DynamicContentPage"));
const DiveSiteDetailRouter = lazy(() => import("./pages/DiveSiteDetailRouter"));
const Results = lazy(() => import("./pages/Results"));
const MapView = lazy(() => import("./pages/MapView"));
const VisibilityOverview = lazy(() => import("./pages/VisibilityOverview"));
const Guide = lazy(() => import("./pages/Guide"));
const SubmitSpot = lazy(() => import("./pages/SubmitSpot"));
const PoiGuide = lazy(() => import("./pages/PoiGuide"));
const PoiDetail = lazy(() => import("./pages/PoiDetail"));
const SpotVisibilityLogs = lazy(() => import("./pages/SpotVisibilityLogs"));
const CourseLibrary = lazy(() => import("./pages/CourseLibrary"));
const DivePlanner = lazy(() => import("./pages/DivePlanner"));
const SubmitPoi = lazy(() => import("./pages/SubmitPoi"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PublicSharePage = lazy(() => import("./pages/PublicSharePage"));
const EmailUnsubscribePage = lazy(() => import("./pages/EmailUnsubscribePage"));
const InviteLanding = lazy(() => import("./pages/InviteLanding"));
// MCP OAuth 2.1 consent screen (Supabase authorization server)
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));

// Auth pages
const Auth = lazy(() => import("./pages/Auth"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Profile = lazy(() => import("./pages/Profile"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));

// Legal (public) pages — backed by `legal_document_public_projection`
const ImprintPage = lazy(() => import("./pages/ImprintPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));

// Community / Buddy pages
const BuddiesPage = lazy(() => import("./pages/BuddiesPage"));
const BuddyMatchPage = lazy(() => import("./pages/BuddyMatchPage"));
const BuddyHubPage = lazy(() => import("./pages/BuddyHubPage"));
const CommunityHubPage = lazy(() => import("./pages/CommunityHubPage"));
const ProfileCodePage = lazy(() => import("./pages/ProfileCodePage"));
const PublicProfilePage = lazy(() => import("./pages/PublicProfilePage"));

// Platform area overviews (foundation teaser surfaces — no productive logic)
const MyDivingOverview = lazy(() => import("./pages/MyDivingOverview"));
const DiveActivity = lazy(() => import("./pages/DiveActivity"));
const DiveActivityImportReview = lazy(() => import("./pages/DiveActivityImportReview"));
const DiveLogDetail = lazy(() => import("./pages/DiveLogDetail"));
const DiveActivityMatches = lazy(() => import("./pages/DiveActivityMatches"));
const CommunityOverview = lazy(() => import("./pages/CommunityOverview"));
const DiscoverOverview = lazy(() => import("./pages/DiscoverOverview"));

// Admin shell (lazy but loaded once when entering /admin)
const AdminLayout = lazy(() => import("@/components/AdminLayout"));

// Admin sub-pages
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminSpotList = lazy(() => import("./pages/AdminSpotList"));
const AdminEnrichment = lazy(() => import("./pages/AdminEnrichment"));
const AdminSpotEnrichmentWorkspace = lazy(() => import("./pages/AdminSpotEnrichmentWorkspace"));
const AdminImport = lazy(() => import("./pages/AdminImport"));
const AdminUserDetail = lazy(() => import("./pages/AdminUserDetail"));
const AdminIdentity = lazy(() => import("./pages/AdminIdentity"));
const AdminProfileModeration = lazy(() => import("./pages/AdminProfileModeration"));
const AdminWaterbodies = lazy(() => import("./pages/AdminWaterbodies"));
const AdminPlaces = lazy(() => import("./pages/AdminPlaces"));
const AdminPois = lazy(() => import("./pages/AdminPois"));
const AdminPoiImportExport = lazy(() => import("./pages/AdminPoiImportExport"));
const AdminVisibilityImport = lazy(() => import("./pages/AdminVisibilityImport"));
const AdminDataHub = lazy(() => import("./pages/AdminDataHub"));
const AdminGeoImportReview = lazy(() => import("./pages/AdminGeoImportReview"));
const AdminVisibilityLogs = lazy(() => import("./pages/AdminVisibilityLogs"));
const AdminOrgDetail = lazy(() => import("./pages/AdminOrgDetail"));
const AdminMembershipLookup = lazy(() => import("./pages/AdminMembershipLookup"));
const MembershipRequestsInbox = lazy(() => import("./pages/MembershipRequestsInbox"));
const AdminSystem = lazy(() => import("./pages/AdminSystem"));
const AdminCorsOrigins = lazy(() => import("./pages/AdminCorsOrigins"));
const AdminInviteEmails = lazy(() => import("./pages/AdminInviteEmails"));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog"));
const AdminHazards = lazy(() => import("./pages/AdminHazards"));
const AdminContentPages = lazy(() => import("./pages/AdminContentPages"));
const AdminLegal = lazy(() => import("./pages/AdminLegal"));
const AdminTranslations = lazy(() => import("./pages/AdminTranslations"));
const AdminCourses = lazy(() => import("./pages/AdminCourses"));
const AdminFieldCapture = lazy(() => import("./pages/AdminFieldCapture"));
const AdminOperations = lazy(() => import("./pages/AdminOperations"));
const AdminEmailHealth = lazy(() => import("./pages/AdminEmailHealth"));
const AdminAccessRoles = lazy(() => import("./pages/AdminAccessRoles"));
const AdminReviewCenter = lazy(() => import("./pages/AdminReviewCenter"));
const AdminMonetization = lazy(() => import("./pages/AdminMonetization"));
const AdminContentAlerts = lazy(() => import("./pages/AdminContentAlerts"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminMonitoring = lazy(() => import("./pages/AdminMonitoring"));
const AdminSystemAlerts = lazy(() => import("./pages/AdminSystemAlerts"));
const AdminNotifications = lazy(() => import("./pages/AdminNotifications"));
const AdminNotificationDeliveries = lazy(() => import("./pages/AdminNotificationDeliveries"));
const AdminCommunityRewards = lazy(() => import("./pages/AdminCommunityRewards"));
const BetaStatus = lazy(() => import("./pages/BetaStatus"));

// Spot Studio (role-gated, accessed from main user nav, NOT under /admin)
const SpotStudioLayout = lazy(() => import("./pages/SpotStudio/Layout"));
const SpotStudioDashboard = lazy(() => import("./pages/SpotStudio/Dashboard"));
const SpotStudioTasks = lazy(() => import("./pages/SpotStudio/Tasks"));
const SpotStudioTaskDetail = lazy(() => import("./pages/SpotStudio/TaskDetail"));
const SpotStudioContributions = lazy(() => import("./pages/SpotStudio/Contributions"));

// ── Route loading fallback ─────────────────────────────────
function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-primary" />
    </div>
  );
}

/**
 * Globale TanStack-Query-Defaults.
 *
 * Hintergrund: Read-Hooks setzen ihren eigenen, fachlich passenden `staleTime`
 * (s. `useDiveData`, `useMapSpots`, etc.). Die Defaults hier definieren das
 * Verhalten für alle Queries, die keinen eigenen Wert mitgeben, und sorgen
 * für ein konsistentes Cache-/Refetch-Verhalten:
 *
 *  - `staleTime: 30s`            – verhindert das aggressive Refetch-Verhalten
 *                                   bei jedem Re-Mount und reduziert Last auf
 *                                   Edge Functions/RLS-RPCs spürbar.
 *  - `gcTime: 5min`              – Caches überleben Tab-Wechsel & schnelle
 *                                   Routenwechsel ohne sofort verworfen zu werden.
 *  - `refetchOnWindowFocus: false` – die App ist eine Admin/PWA-artige Erfahrung;
 *                                   Realtime-Updates kommen über die dedizierte
 *                                   `useSpotProjectionRealtime`-Bridge.
 *  - `retry: 1`                  – wir retryen einmal; Edge-Function-Fehler
 *                                   sind in der Regel deterministisch.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});

function RealtimeBridge() {
  useSpotProjectionRealtime();
  return null;
}

/**
 * Defers mounting realtime bridges until after first paint + browser idle.
 * This prevents the WebSocket handshake from racing initial page load
 * (which can surface as ERR_NAME_NOT_RESOLVED in Lighthouse / cold DNS
 * scenarios) and keeps realtime off the critical rendering path.
 * Live sync still activates within ~1–2s of load.
 */
function DeferredRealtime({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const arm = () => {
      const w = window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      };
      const schedule = w.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1500));
      schedule(() => {
        if (!cancelled) setReady(true);
      }, { timeout: 3000 });
    };
    if (document.readyState === "complete") {
      arm();
    } else {
      window.addEventListener("load", arm, { once: true });
    }
    return () => {
      cancelled = true;
      window.removeEventListener("load", arm);
    };
  }, []);
  return ready ? <>{children}</> : null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <DeferredRealtime>
      <RealtimeBridge />
    </DeferredRealtime>
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
      storageKey="scuvio-theme"
    >
    <AuthProvider>
    <DeferredRealtime>
      <NotificationRealtimeBridge />
    </DeferredRealtime>
    <PreviewProvider>
    <I18nProvider>
      <UnitsProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <DebugLayerProvider>
          <PreviewBanner />
          <OfflineBanner />
          <OfflineSync />
          <Navbar />
          <MobileTopBar />
          <GlobalContentAlertsBanner />
          <VersionBadge />
          <BetaGate>
          <PageTransition>
          <AppErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/page/:slug" element={<DynamicContentPage />} />
            <Route path="/results" element={<FeatureRoute feature="dive_guide"><Results /></FeatureRoute>} />
            <Route path="/site/:id" element={<FeatureRoute feature="dive_guide"><DiveSiteDetailRouter /></FeatureRoute>} />
            <Route path="/site/:id/visibility" element={<FeatureRoute feature="visibility_reports"><SpotVisibilityLogs /></FeatureRoute>} />
            {/* Slug-friendly alias of /site/:id — accepts the spot slug. */}
            <Route path="/spot/:id" element={<FeatureRoute feature="dive_guide"><DiveSiteDetailRouter /></FeatureRoute>} />
            <Route path="/spot/:id/visibility" element={<FeatureRoute feature="visibility_reports"><SpotVisibilityLogs /></FeatureRoute>} />
            <Route path="/map" element={<FeatureRoute feature="map"><MapView /></FeatureRoute>} />
            <Route path="/providers" element={<FeatureRoute feature="pois"><PoiGuide /></FeatureRoute>} />
            <Route path="/providers/:id" element={<FeatureRoute feature="pois"><PoiDetail /></FeatureRoute>} />
            <Route path="/guide" element={<FeatureRoute feature="dive_guide"><Guide /></FeatureRoute>} />
            <Route path="/courses" element={<CourseLibrary />} />
            <Route path="/about" element={<DynamicContentPage slug="ueber-uns" />} />
            <Route path="/vision" element={<DynamicContentPage slug="vision" />} />
            <Route path="/our-partners" element={<DynamicContentPage slug="partner" />} />
            <Route path="/dive-clubs" element={<DynamicContentPage slug="tauchclubs" />} />
            <Route path="/dive-schools" element={<DynamicContentPage slug="tauchschulen" />} />
            <Route path="/become-partner" element={<DynamicContentPage slug="partner-werden" />} />
            {/* Legal pages — published via /admin/legal, served from the
                public projection. Both English and German routes are
                registered; English aliases keep existing footer links and
                external references stable. */}
            <Route path="/impressum" element={<ImprintPage />} />
            <Route path="/datenschutz" element={<PrivacyPage />} />
            <Route path="/nutzungsbedingungen" element={<TermsPage />} />
            <Route path="/imprint" element={<ImprintPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/contact" element={<DynamicContentPage slug="kontakt" />} />
            {/* Public share landing — anonymous-friendly. */}
            <Route path="/s/:shareSlug" element={<PublicSharePage />} />
            <Route path="/unsubscribe" element={<EmailUnsubscribePage />} />
            <Route path="/invite/:code" element={<InviteLanding />} />
            <Route path="/support" element={<DynamicContentPage slug="scuvio-unterstuetzen" />} />
            <Route path="/visibility" element={<FeatureRoute feature="visibility_reports"><VisibilityOverview /></FeatureRoute>} />
            <Route path="/report" element={<FeatureRoute feature="visibility_reports"><VisibilityOverview /></FeatureRoute>} />
            <Route path="/community/buddynet" element={
              <FeatureRoute feature="buddyhub"><ProtectedRoute><BuddiesPage /></ProtectedRoute></FeatureRoute>
            } />
            <Route path="/community/buddymatch" element={<FeatureRoute feature="buddyhub"><BuddyMatchPage /></FeatureRoute>} />
            <Route path="/community/buddyhub" element={<FeatureRoute feature="buddyhub"><BuddyHubPage /></FeatureRoute>} />
            <Route path="/community/buddies" element={
              <FeatureRoute feature="buddyhub"><ProtectedRoute><BuddiesPage /></ProtectedRoute></FeatureRoute>
            } />
            {/* /community is now an area overview (teaser surface);
                the BuddyNet sub-route remains reachable directly. */}
            <Route path="/community" element={<CommunityOverview />} />
            <Route path="/community/hub" element={<Navigate to="/community/buddynet" replace />} />
            <Route path="/community/match" element={<Navigate to="/community/buddymatch" replace />} />
            {/* Personal diving area — overview only, no productive feature
                logic. Sub-routes are intentionally not registered yet to
                avoid implying functionality that does not exist. */}
            <Route path="/my-diving" element={<ProtectedRoute><MyDivingOverview /></ProtectedRoute>} />
            <Route path="/my-diving/dive-activity" element={<ProtectedRoute><DiveActivity /></ProtectedRoute>} />
            <Route path="/my-diving/dive-activity/logbook" element={<ProtectedRoute><DiveActivity /></ProtectedRoute>} />
            <Route path="/my-diving/dive-activity/imports" element={<ProtectedRoute><DiveActivity /></ProtectedRoute>} />
            <Route path="/my-diving/dive-activity/imports/:importId/review" element={<ProtectedRoute><DiveActivityImportReview /></ProtectedRoute>} />
            <Route path="/my-diving/dive-activity/logs/:diveLogId" element={<ProtectedRoute><DiveLogDetail /></ProtectedRoute>} />
            <Route path="/my-diving/dive-activity/matches" element={<ProtectedRoute><DiveActivity /></ProtectedRoute>} />
            <Route path="/my-diving/dive-activity/statistics" element={<ProtectedRoute><DiveActivity /></ProtectedRoute>} />
            {/* Backward-compat: notifications and external links still
                point at the old top-level matches route. */}
            <Route path="/my-diving/matches" element={<Navigate to="/my-diving/dive-activity/matches" replace />} />
            {/* `/discover` is a stable IA alias that renders an explicit
                area overview (Spots / Visibility / POIs / Map cards).
                The home page (`/`) remains the primary discovery
                surface — `/discover` exists so the IA contract has a
                first-class landing surface for the "Entdecken" area. */}
            <Route path="/discover" element={<DiscoverOverview />} />
            <Route path="/p/:profileCode" element={<ProfileCodePage />} />
            <Route path="/u/:slug" element={<PublicProfilePage />} />
            <Route path="/submit-spot" element={
              <ProtectedRoute><SubmitSpot /></ProtectedRoute>
            } />
            <Route path="/submit-poi" element={
              <ProtectedRoute><SubmitPoi /></ProtectedRoute>
            } />
            <Route path="/auth" element={<Auth />} />
            <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
            <Route path="/beta-status" element={<BetaStatus />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/profile" element={
              <ProtectedRoute><Profile /></ProtectedRoute>
            } />
            <Route path="/notifications" element={
              <ProtectedRoute><NotificationsPage /></ProtectedRoute>
            } />
            <Route path="/memberships/inbox" element={
              <ProtectedRoute><MembershipRequestsInbox /></ProtectedRoute>
            } />
            {/* Spot Studio — role-gated shell (admin/super_admin/spot_admin/
                verifier/moderator/local_guide/partner). Access enforcement
                lives inside SpotStudioLayout via useSpotStudioAccess. */}
            <Route path="/spot-studio" element={<ProtectedRoute><SpotStudioLayout /></ProtectedRoute>}>
              <Route index element={<SpotStudioDashboard />} />
              <Route path="tasks" element={<SpotStudioTasks />} />
              <Route path="tasks/:taskId" element={<SpotStudioTaskDetail />} />
              <Route path="contributions" element={<SpotStudioContributions />} />
            </Route>
            <Route path="/admin" element={
              <ProtectedRoute featureKey="admin">
                <AdminLayout />
              </ProtectedRoute>
            }>
              {/* Sub-routes: parent route already gates on featureKey="admin".
                  Inner ProtectedRoute kept only as a defensive identity guard. */}
              <Route index element={<AdminDashboard />} />
              <Route path="spots" element={<AdminSpotList />} />
              <Route path="enrichment" element={<AdminEnrichment />} />
              <Route path="enrichment/:spotId" element={<AdminSpotEnrichmentWorkspace />} />
              {/* Retired routes: /admin/spots-v2, /admin/spot-quality,
                  /admin/v2-migration, /admin/v2-migration/:spotId,
                  /admin/spots/maintenance, /admin/review.
                  Spot-Migration ist abgeschlossen; alle Migrations-Tools
                  wurden entfernt. Review-Quellen leben unter /admin/review-center.
                  Hard 404 via the catch-all NotFound route below. */}
              <Route path="import" element={<AdminImport />} />
              <Route path="visibility-import" element={<AdminVisibilityImport />} />
              <Route path="data-hub" element={<AdminDataHub />} />
              <Route path="import-geo-review" element={<AdminGeoImportReview />} />
              <Route path="visibility-logs" element={<AdminVisibilityLogs />} />
              {/* Identity Hub: Benutzer, Organisationen, Mitgliedschaften */}
              <Route path="identity" element={<AdminIdentity />} />
              <Route path="identity/users/:userId" element={<AdminUserDetail />} />
              <Route path="identity/organisations/:orgId" element={<AdminOrgDetail />} />
              <Route path="identity/membership-lookup" element={<AdminMembershipLookup />} />
              {/* Community Profile Moderation lives under /admin/community/*.
                  Legacy /admin/identity/profile-moderation and /admin/profile-moderation
                  are kept as redirects only — no visible navigation entry. */}
              <Route path="community/profile-moderation" element={<AdminProfileModeration />} />
              <Route path="identity/profile-moderation" element={<Navigate to="/admin/community/profile-moderation" replace />} />
              <Route path="profile-moderation" element={<Navigate to="/admin/community/profile-moderation" replace />} />
              {/* Legacy Identity deep-links — kept for backwards compatibility */}
              <Route path="users" element={<Navigate to="/admin/identity?tab=users" replace />} />
              <Route path="users/:userId" element={<AdminUserDetail />} />
              <Route path="data-reports" element={<Navigate to="/admin/review-center?source=data_report" replace />} />
              <Route path="alerts" element={<Navigate to="/admin/content/alerts" replace />} />
              <Route path="content/alerts" element={<AdminContentAlerts />} />
              <Route path="waterbodies" element={<AdminWaterbodies />} />
              <Route path="places" element={<AdminPlaces />} />
              <Route path="pois" element={<AdminPois />} />
              <Route path="pois/import-export" element={<AdminPoiImportExport />} />
              <Route path="organisations" element={<Navigate to="/admin/identity?tab=organisations" replace />} />
              <Route path="organisations/:orgId" element={<AdminOrgDetail />} />
              <Route path="memberships" element={<Navigate to="/admin/identity?tab=memberships" replace />} />
              <Route path="settings" element={<Navigate to="/admin/system" replace />} />
              <Route path="system" element={<AdminSystem />} />
              <Route path="cors-origins" element={<AdminCorsOrigins />} />
              <Route path="invite-emails" element={<AdminInviteEmails />} />
              <Route path="audit" element={<AdminAuditLog />} />
              <Route path="hazards" element={<AdminHazards />} />
              <Route path="content-pages" element={<AdminContentPages />} />
              <Route path="legal" element={<AdminLegal />} />
              {/* Monetization Hub: Pläne, Entitlements, Abonnemente, Freischaltungen */}
              <Route path="monetization" element={<AdminMonetization />} />
              <Route path="entitlements" element={<Navigate to="/admin/monetization?tab=entitlements" replace />} />
              <Route path="subscription-plans" element={<Navigate to="/admin/monetization?tab=plans" replace />} />
              <Route path="beta-programs" element={<Navigate to="/admin/access?tab=beta" replace />} />
              <Route path="claims" element={<Navigate to="/admin/review-center?source=poi_claim" replace />} />
              <Route path="submissions" element={<Navigate to="/admin/review-center?source=spot_submission" replace />} />
              <Route path="poi-submissions" element={<Navigate to="/admin/review-center?source=poi_submission" replace />} />
              <Route path="review-hub" element={<Navigate to="/admin/review-center" replace />} />
              <Route path="field-capture" element={<AdminFieldCapture />} />
              <Route path="field-capture-review" element={<Navigate to="/admin/review-center?source=field_capture" replace />} />
              <Route path="feature-rules" element={<Navigate to="/admin/access?tab=rules" replace />} />
              <Route path="content-rules" element={<Navigate to="/admin/access?tab=rules" replace />} />
              {/* Access Inspector ersetzt den ehemaligen Access-Debug-Screen */}
              <Route path="access-debug" element={<Navigate to="/admin/access?tab=inspector" replace />} />
              <Route path="operations" element={<AdminOperations />} />
              <Route path="email-health" element={<AdminEmailHealth />} />
              <Route path="jobs" element={<Navigate to="/admin/operations?tab=queues&queue=jobs" replace />} />
              <Route path="operability" element={<Navigate to="/admin/operations?tab=overview" replace />} />
              <Route path="settings/*" element={<Navigate to="/admin/system" replace />} />
              <Route path="access" element={<AdminAccessRoles />} />
              <Route path="catalog" element={<AdminCourses />} />
              <Route path="translations" element={<AdminTranslations />} />
              <Route path="audit-logs" element={<Navigate to="/admin/audit" replace />} />
              <Route path="user-roles" element={<Navigate to="/admin/access?tab=assignments" replace />} />
              <Route path="roles" element={<Navigate to="/admin/access?tab=definitions" replace />} />
              <Route path="courses" element={<Navigate to="/admin/catalog?tab=courses" replace />} />
              <Route path="certifications" element={<Navigate to="/admin/catalog" replace />} />
              <Route path="spot-contributions" element={<Navigate to="/admin/review-center?source=spot_contribution" replace />} />
              <Route path="spot-notifications" element={<Navigate to="/admin/review-center" replace />} />
              <Route path="review-center" element={<AdminReviewCenter />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="monitoring" element={<AdminMonitoring />} />
              <Route path="system-alerts" element={<AdminSystemAlerts />} />
              <Route path="notifications" element={<AdminNotifications />} />
              <Route path="notification-deliveries" element={<AdminNotificationDeliveries />} />
              {/* Community → Rewards (Backend-only Verwaltung) */}
              <Route path="community/rewards" element={<AdminCommunityRewards />} />
              {/* Legacy redirect: alter direkter Pfad → neuer Community-Pfad */}
              <Route path="rewards" element={<Navigate to="/admin/community/rewards" replace />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </AppErrorBoundary>
          </PageTransition>
          </BetaGate>
          <Footer />
          <MobileCompactFooter />
          <BottomNav />
          <CookieBanner />
          <DebugLayerHost />
          </DebugLayerProvider>
        </BrowserRouter>
      </TooltipProvider>
      </UnitsProvider>
    </I18nProvider>
    </PreviewProvider>
    </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
